"use client";

import React, { FC, useEffect, useState } from "react";
import { Route } from "next";
import { useParams, usePathname, useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";
import api from "@/app/lib/reducers/api";

/**
 * Coeur « favori » de la fiche tour (îlot client : la page est rendue côté serveur, sans
 * utilisateur).
 *
 * - Connecté : l'état est relu à l'affichage (GET /api/tour/:uuid/user-favourite, compte
 *   tiré du jeton), le clic bascule (PUT …/user-favourite/:userUuid, même route que les
 *   cartes de tour) avec mise à jour immédiate, annulée si le serveur refuse.
 * - Pas connecté : connexion, puis retour sur cette fiche (`?next=`).
 *
 * `overlay` : pastille ronde posée sur la photo (mobile) ; `inline` : bouton à côté du titre.
 */
export interface TourFavouriteButtonProps {
  tourUuid: string;
  variant?: "overlay" | "inline";
  className?: string;
}

const Heart: FC<{ filled: boolean; size: number }> = ({ filled, size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? "var(--g-green-ada)" : "none"}
    stroke="var(--g-green-ada)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const Spinner: FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="var(--g-green-soft)" strokeWidth="3" opacity=".35" />
    <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="var(--g-green-ada)" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

// Un jeton est enregistré dans le navigateur : la session va se recharger. Tant que
// l'utilisateur n'est pas encore là, on ne sait pas encore s'il est connecté.
const hasStoredSession = () => {
  try {
    return Boolean(window.localStorage.getItem("access_token"));
  } catch (err) {
    return false;
  }
};
// Au-delà, la session ne viendra plus (jeton périmé) : on considère le visiteur déconnecté.
const SESSION_WAIT_MS = 4000;

const TourFavouriteButton: FC<TourFavouriteButtonProps> = ({ tourUuid, variant = "inline", className = "" }) => {
  const { t } = useTranslation("guydeez");
  const router = useRouter();
  const pathname = usePathname();
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const user = useAppSelector((state) => state.auth.user);
  const userUuid = user?.uuid;
  const [isFavourite, setIsFavourite] = useState(false);
  const [busy, setBusy] = useState(false);
  // Tant que l'état n'est pas connu, le bouton n'affiche ni « ajouter » ni « enregistré » :
  // un indicateur de chargement, sans action. Vrai dès le rendu serveur (pas de flash).
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    if (!tourUuid) {
      setLoaded(true);
      return undefined;
    }
    if (!userUuid) {
      setIsFavourite(false);
      if (!hasStoredSession()) {
        setLoaded(true);
        return undefined;
      }
      const giveUp = setTimeout(() => setLoaded(true), SESSION_WAIT_MS);
      return () => clearTimeout(giveUp);
    }
    let alive = true;
    api
      .get(`/api/tour/${tourUuid}/user-favourite`)
      .then((res) => {
        if (alive) setIsFavourite(Boolean(res.data?.isFavourite));
      })
      .catch(() =>
        // Backend sans cette route (deploye apres le site) : on relit la liste des favoris
        // du compte. Sans etat juste, le clic — une bascule — RETIRERAIT un favori en
        // affichant l'inverse.
        api
          .get(`/api/user/${userUuid}/favourite-tours`, { params: { page: 1, size: 200 } })
          .then((res) => {
            const tours = res.data?.favouriteTours?.tours || [];
            if (alive) setIsFavourite(tours.some((tour: { uuid?: string }) => tour?.uuid === tourUuid));
          })
          .catch(() => {
            // Etat inconnu : le coeur reste vide.
          })
      )
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [userUuid, tourUuid]);

  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!userUuid) {
      router.push(`/${lang}/${currency}/login?next=${encodeURIComponent(pathname || "/")}` as Route);
      return;
    }
    if (busy || !loaded) return;
    const previous = isFavourite;
    setIsFavourite(!previous);
    setBusy(true);
    try {
      await api.put(`/api/tour/${tourUuid}/user-favourite/${userUuid}`);
    } catch (err) {
      setIsFavourite(previous);
    } finally {
      setBusy(false);
    }
  };

  const label = isFavourite
    ? t("tour_favourite_remove", { defaultValue: "Remove from favourites" })
    : t("tour_favourite_add", { defaultValue: "Add to favourites" });

  if (variant === "overlay") {
    // Pastille posée sur la photo (mobile) : coeur + texte court, verte une fois enregistré.
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={!loaded}
        aria-busy={!loaded}
        aria-pressed={isFavourite}
        title={label}
        className={`inline-flex items-center gap-1.5 ${className}`}
        style={{
          height: 36,
          padding: "0 12px",
          borderRadius: 18,
          background: isFavourite && loaded ? "#EAF7EE" : "rgba(255,255,255,.95)",
          border: isFavourite && loaded ? "1px solid var(--g-green-ada)" : "1px solid transparent",
          boxShadow: "0 2px 8px rgba(15,26,20,.12)",
          color: isFavourite && loaded ? "var(--g-green-ink)" : "var(--g-ink-900)",
          fontSize: 13,
          fontWeight: 600,
          cursor: loaded ? "pointer" : "default",
        }}
      >
        {loaded ? (
          <>
            <Heart filled={isFavourite} size={15} />
            {isFavourite
              ? t("tour_favourite_saved", { defaultValue: "Saved to favourites" })
              : t("tour_favourite_add", { defaultValue: "Add to favourites" })}
          </>
        ) : (
          <>
            <Spinner size={15} />
            <span className="animate-pulse" style={{ display: "inline-block", width: 90, height: 10, borderRadius: 5, background: "rgba(79, 191, 110, .18)" }} />
          </>
        )}
      </button>
    );
  }

  if (!loaded) {
    // Même taille que le bouton final : rien ne bouge quand l'état arrive.
    return (
      <button
        type="button"
        disabled
        aria-busy="true"
        aria-label={t("loading", { defaultValue: "Loading" })}
        className={`inline-flex items-center gap-2 shrink-0 ${className}`}
        style={{
          border: "1px solid var(--g-green-soft)",
          background: "#fff",
          borderRadius: 22,
          padding: "8px 14px",
          fontSize: 14,
          cursor: "default",
        }}
      >
        <Spinner size={16} />
        <span style={{ display: "inline-flex", alignItems: "center", height: 20 }}>
          <span className="animate-pulse" style={{ display: "inline-block", width: 150, height: 12, borderRadius: 6, background: "rgba(79, 191, 110, .18)" }} />
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      aria-pressed={isFavourite}
      title={label}
      className={`inline-flex items-center gap-2 shrink-0 ${className}`}
      // Verts du site (design system) : bordure verte claire, puis fond et texte verts une
      // fois le tour enregistré, pour se distinguer d'un coup d'oeil.
      style={{
        border: isFavourite ? "1px solid var(--g-green-ada)" : "1px solid var(--g-green-soft)",
        background: isFavourite ? "rgba(79, 191, 110, .14)" : "#fff",
        borderRadius: 22,
        padding: "8px 14px",
        fontSize: 14,
        fontWeight: 600,
        color: isFavourite ? "var(--g-green-ink)" : "var(--g-ink-900)",
        cursor: "pointer",
        transition: "background .15s, border-color .15s, color .15s",
      }}
    >
      <Heart filled={isFavourite} size={16} />
      {isFavourite
        ? t("tour_favourite_saved", { defaultValue: "Saved to favourites" })
        : t("tour_favourite_save", { defaultValue: "Add tour as favourite" })}
    </button>
  );
};

export default TourFavouriteButton;
